// Pools competitive_comparison assessment rows from both sides of each
// competitor pair. A topic's own rows are scored with the topic as subject;
// the rows fetched from a competitor's topic are scored with the competitor
// as subject, so any of those that compare back against this topic get their
// rating sign flipped and their comparator re-keyed before being pooled.
// Without this, each company page only counts its own feed — which
// over-samples "use the rival instead" posts — and both sides of a pair can
// look like they're losing to each other.

const normalizeName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Comparator names are LLM free text ("Higgsfield AI" vs the "Higgsfield"
// topic), so equality is checked on normalized names with containment
// allowed — but only for names long enough that containment can't fire on a
// coincidence like "art" inside "openart".
const namesMatch = (a, b) => {
    const na = normalizeName(a);
    const nb = normalizeName(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const shorter = na.length <= nb.length ? na : nb;
    const longer = na.length <= nb.length ? nb : na;
    return shorter.length >= 4 && longer.includes(shorter);
};

const postKey = (row) => row?.url || row?.post_id || row?.id || null;

const ratingOf = (row) => {
    const r = parseFloat(row?.rating);
    return Number.isNaN(r) ? null : r;
};

const rowComparator = (row) => row?.comparator || row?.details?.competitor;

// Merge one topic's own rows with the mirrored rows from its competitors.
// Mirrored rows carry the main topic's id so downstream product filters
// (item.topic_id === selectedProduct) keep them.
export const mergeTopicCompetitiveAssessments = (topic) => {
    const ownRows = Array.isArray(topic?.assessments?.competitiveComparison)
        ? topic.assessments.competitiveComparison
        : [];
    const subjectNames = [topic?.name, topic?.topic].filter(Boolean);

    const merged = [...ownRows];

    // Index own rows by (competitor, post) so a post assessed under both
    // topics is reconciled instead of counted twice.
    const ownIndex = new Map();
    ownRows.forEach((row, i) => {
        const key = postKey(row);
        const comparator = rowComparator(row);
        if (key && comparator) ownIndex.set(`${normalizeName(comparator)}|${key}`, i);
    });

    (topic?.competitors || []).forEach(comp => {
        const compRows = comp?.assessments?.competitiveComparison;
        if (!Array.isArray(compRows)) return;
        const compName = comp.topic || comp.alt_name;
        if (!compName) return;

        compRows.forEach(row => {
            // Only the competitor's rows that compare back against this topic
            // belong to this pair; the rest are its other rivalries.
            if (!subjectNames.some(n => namesMatch(rowComparator(row), n))) return;
            const rating = ratingOf(row);
            if (rating == null) return;

            const mirrored = {
                ...row,
                topic_id: topic.id,
                comparator: compName,
                rating: -rating,
                details: { ...(row.details || {}), competitor: compName },
                mirrored: true,
            };

            const key = postKey(row);
            const ownIdx = key != null
                ? ownIndex.get(`${normalizeName(compName)}|${key}`)
                : undefined;
            if (ownIdx !== undefined) {
                // Same post scored from both sides: average the two reads.
                // Agreeing reads keep their direction; contradictory ones
                // cancel toward neutral instead of counting as a loss for
                // both brands.
                const own = merged[ownIdx];
                const ownRating = ratingOf(own);
                merged[ownIdx] = {
                    ...own,
                    rating: ownRating == null
                        ? mirrored.rating
                        : (ownRating + mirrored.rating) / 2,
                };
                return;
            }
            merged.push(mirrored);
        });
    });

    return merged;
};

// Drop-in replacement for the old inline
// Object.values(topicMap).filter(...).flatMap(...) pattern, except a topic
// whose own feed has no comparison rows still contributes the mirrored rows
// its competitors hold about it.
export const mergeCompetitiveAssessments = (topicMap) =>
    Object.values(topicMap || {})
        .filter(t =>
            t?.assessments?.competitiveComparison ||
            t?.competitors?.some(c => c?.assessments?.competitiveComparison?.length))
        .flatMap(mergeTopicCompetitiveAssessments);
